import { motion } from 'framer-motion'
import ProtocolWhatsappTestimonials from '@/components/ProtocolWhatsappTestimonials'

const JORNADA_COMPLETA_LINK = 'https://pay.hotmart.com/O105657366F?off=x4c01y5z'

const ACCENT = '#B9A3D9'
const ACCENT_SOFT = '#D4C3EA'

const CYCLES = [
  { emoji: '🌱', title: 'Raízes', desc: 'Liberação de programações familiares e crenças herdadas.' },
  { emoji: '🌿', title: 'Padrões', desc: 'Interrupção de loops de autossabotagem e repetição.' },
  { emoji: '🌾', title: 'Feridas', desc: 'Liberação do não resolvido e ressignificação de memórias.' },
  { emoji: '🍃', title: 'Reconexão', desc: 'Liberte-se da identidade que o passado construiu e reconecte com quem você veio ser.' },
  { emoji: '🌸', title: 'Merecimento', desc: 'Dissolva a crença de que não merece e ancore uma nova realidade de plenitude e abundância.' },
  { emoji: '🍎', title: 'Crescimento', desc: 'Sustente sua transformação, expanda em todas as áreas e floresça de forma permanente.' },
]

const FAQ_ITEMS = [
  {
    q: 'Como vou receber o Protocolo de Liberação e Cocriação e tudo que contempla cada ciclo?',
    a: 'Assim que você se inscrever receberá uma mensagem no WhatsApp com o link do formulário para preencher. A partir do que você revelar no formulário, crio o seu mapa com tudo que está limitando você, as frases de elevação do seu ciclo e gravo a sua sessão de liberação em áudio. Tudo é entregue por e-mail em até 3 a 5 dias úteis. Antes de completar os 21 dias do ciclo atual, você já recebe o formulário do próximo ciclo, para que sua jornada continue sem interrupção.',
  },
  {
    q: 'Como vou ter acesso ao formulário?',
    a: 'Após a confirmação do pagamento, o link do formulário será enviado diretamente para o seu WhatsApp.',
  },
  {
    q: 'Terei suporte durante o processo?',
    a: 'Sim. Você pode enviar dúvidas por e-mail ou diretamente pelo WhatsApp de Mônica Maria durante todo o ciclo.',
  },
  {
    q: 'Preciso ter algum conhecimento prévio em espiritualidade ou terapia?',
    a: 'Não. O Protocolo foi criado para funcionar independentemente do nível de conhecimento. Tudo que você precisa é de fones de ouvido e disposição para ouvir a sessão antes de dormir.',
  },
  {
    q: 'E se eu dormir no meio da sessão?',
    a: 'Isso é completamente natural e faz parte do processo. O estado de sonolência favorece a liberação porque as resistências do consciente se baixam. Quer você ouça acordado ou dormindo, a transformação acontece.',
  },
  {
    q: 'Posso ouvir a sessão mais de uma vez?',
    a: 'Pode, desde que preferencialmente deite e fique em estado relaxado. O recomendado é ouvir no horário em que for dormir, pois tudo que foi ouvido vai continuar reverberando durante toda a noite.',
  },
  {
    q: 'A sessão é gerada por inteligência artificial?',
    a: 'Não. Cada sessão é gravada individualmente por Mônica Maria com presença e intenção direcionadas especificamente para o seu campo vibracional. É a voz, a energia e a conexão dela com o que o seu campo revelou.',
  },
  {
    q: 'Posso fazer o Protocolo se já faço outras terapias ou práticas espirituais?',
    a: 'Sim. O Protocolo complementa qualquer processo que você já esteja fazendo. Na verdade, muitas pessoas que chegam já percorreram vários caminhos de cura e encontram no Protocolo o que ainda estava faltando: a liberação da raiz interna que mantém os padrões limitantes.',
  },
]

interface Props {
  onBack?: () => void
}

function FaqItem({ q, a }: { q: string; a: string }) {
  return (
    <div
      className="rounded-sm p-5 sm:p-6"
      style={{
        background: 'linear-gradient(135deg, rgba(185,163,217,0.07) 0%, rgba(185,163,217,0.02) 100%)',
        border: '1px solid rgba(185,163,217,0.18)',
      }}
    >
      <p
        className="font-body mb-3"
        style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 600, lineHeight: 1.5, color: ACCENT_SOFT }}
      >
        {q}
      </p>
      <p
        className="font-body"
        style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', fontWeight: 400, lineHeight: 1.75, color: 'rgba(232,224,213,0.72)' }}
      >
        {a}
      </p>
    </div>
  )
}

function Divider() {
  return (
    <div
      className="h-px w-full my-12 md:my-16"
      style={{ background: 'linear-gradient(90deg, transparent, rgba(185,163,217,0.35), transparent)' }}
    />
  )
}

function PageProtocol({ onBack }: Props) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -16 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="w-full max-w-3xl mx-auto px-5 md:px-0"
    >

      {/* ── BLOCO 1 — HEADLINE ── */}
      <div className="text-center mb-12 md:mb-16">
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
          className="uppercase font-body mb-5"
          style={{ fontSize: '1rem', letterSpacing: '0.4em', lineHeight: 1.7, color: 'rgba(212,195,234,0.65)' }}
        >
          Protocolo de Liberação e Cocriação
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.2 }}
          className="font-display text-[var(--ink)]"
          style={{
            fontSize: 'clamp(1.9rem, 5vw, 2.8rem)',
            fontWeight: 600,
            lineHeight: 1.12,
            color: ACCENT_SOFT,
          }}
        >
          Enquanto você dorme, suas dores mais antigas estão sendo liberadas, uma por uma.
        </motion.h1>
        <motion.div
          initial={{ width: 0, opacity: 0 }}
          animate={{ width: 80, opacity: 1 }}
          transition={{ duration: 1.2, delay: 0.45 }}
          className="h-px mx-auto mt-8"
          style={{ background: `linear-gradient(90deg, transparent, ${ACCENT}, transparent)` }}
        />
      </div>

      {/* ── BLOCO 2 — INTRODUÇÃO ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.25 }}
        className="mb-12 md:mb-16"
      >
        <div className="space-y-5 max-w-2xl mx-auto">
          <p
            className="font-body"
            style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.85, color: 'rgba(232,224,213,0.78)' }}
          >
            Uma sessão de liberação gravada em áudio exclusivamente para você, baseada nas suas memórias, suas frases e suas feridas. Não é inteligência artificial. É minha voz, minha presença e minha conexão com o seu campo vibracional. Minha voz, ao ser escutada, atua com uma poderosa vibração de liberação, alinhamento e elevação. E isso será vivido por você durante <span className="numerals">126</span> dias de transformação contínua, em <span className="numerals">6</span> ciclos, sendo cada ciclo de <span className="numerals">21</span> dias.
          </p>

          <h2
            className="font-display text-center pt-4"
            style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', fontWeight: 600, lineHeight: 1.18, color: ACCENT_SOFT }}
          >
            Você já tentou de tudo.
          </h2>

          <p
            className="font-body text-center italic"
            style={{
              fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)',
              fontWeight: 400,
              lineHeight: 2.2,
              letterSpacing: '0.08em',
              color: 'rgba(232,224,213,0.78)',
            }}
          >
            Terapia. &nbsp; Orações. &nbsp; Afirmações.
            <br />
            Decretos. &nbsp; Cursos. &nbsp; Práticas espirituais.
          </p>

          {[
            'E ainda assim algo continua igual. Um padrão que se repete. Uma sensação que não vai embora. Uma versão de você que ainda não conseguiu ser livre.',
            'Isso não é falta de esforço. É porque a raiz ainda não foi tocada.',
            'Existem inúmeras meditações e elas funcionam. Mas o que lhe apresento é uma sessão de liberação gravada em áudio exclusivamente feita para você. Quando algo é criado a partir das suas memórias, das suas palavras e das suas feridas específicas, você não apenas relaxa. Você sente a transformação sendo conduzida diretamente para o que precisava ser tocado.',
            'Minha voz carrega uma frequência de cura. Durante a gravação, me conecto com o seu campo vibracional, e isso faz com que cada palavra pronunciada atue não apenas na sua mente, mas na sua vibração mais profunda. É cura vibracional entregue enquanto você dorme.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{
                fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)',
                fontWeight: i === 1 ? 600 : 400,
                lineHeight: 1.85,
                color: i === 1 ? 'var(--ink)' : 'rgba(232,224,213,0.78)',
              }}
            >
              {text}
            </p>
          ))}
        </div>
      </motion.div>

      {/* ── BLOCO 3 — IDENTIFICAÇÃO DA DOR ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <p
          className="font-body mb-8 text-center"
          style={{ fontSize: 'clamp(1.25rem, 2.8vw, 1.45rem)', fontWeight: 600, lineHeight: 1.5, color: 'var(--ink)' }}
        >
          Esses são os <span className="numerals">3</span> maiores motivos de você viver ciclos repetitivos e negativos:
        </p>
        <div className="space-y-5">
          {[
            'Você está parado no tempo e preso em pensamentos, dores, crenças, frases ou memórias negativas.',
            'Você não sabe equilibrar a mente em situações de caos, bloqueios e desequilíbrios.',
            'Você fica cada vez mais emaranhado, limitado e tudo se repete enquanto não romper com o que já passou, mas continua vivo e vibrando dentro de você.',
          ].map((text, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-sm p-5 sm:p-6"
              style={{
                background: 'linear-gradient(135deg, rgba(185,163,217,0.08) 0%, rgba(185,163,217,0.02) 100%)',
                border: '1px solid rgba(185,163,217,0.2)',
              }}
            >
              <span style={{ color: ACCENT, fontSize: '1.1rem', flexShrink: 0, marginTop: '2px' }}>✦</span>
              <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.85)' }}>
                {text}
              </p>
            </div>
          ))}
        </div>
      </motion.div>

      <Divider />

      {/* ── BLOCO 4 — AUTORIDADE ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.35 }}
        className="mb-12 md:mb-16"
      >
        <div className="flex justify-center mb-8">
          <div
            className="w-28 h-28 sm:w-32 sm:h-32 rounded-full overflow-hidden"
            style={{
              border: `2px solid ${ACCENT}66`,
              boxShadow: `0 0 24px ${ACCENT}22`,
            }}
          >
            <img
              src="https://static.lumi.new/66/66a59164029fe3389411bfef927c8644.webp"
              alt="Mônica Maria — Terapeuta Vibracional"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="text-center mb-8">
          <p
            className="font-display mb-1"
            style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.9rem)', fontWeight: 600, lineHeight: 1.2, color: 'var(--ink)' }}
          >
            Mônica Maria
          </p>
          <p
            className="font-body"
            style={{ fontSize: 'clamp(1.05rem, 2.2vw, 1.15rem)', fontWeight: 400, lineHeight: 1.6, color: 'rgba(212,195,234,0.7)', fontStyle: 'italic' }}
          >
            Terapeuta Vibracional e Especialista em Leitura do Campo Vibracional
          </p>
        </div>

        <div className="space-y-5 max-w-2xl mx-auto">
          {[
            'Desde 2017 conduzo pessoas que já percorreram muitos caminhos de cura, terapia, constelação, coaching e práticas espirituais, e que ainda carregam padrões repetitivos e limitantes.',
            'São quase 10 anos, mais de 15 cursos lançados e milhares de pessoas transformadas.',
            'Meu trabalho é revelar o que está invisível e liberar o que ainda prende.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.78)', textAlign: 'center' }}
            >
              {i === 1 ? (
                <>
                  São quase <span className="numerals">10</span> anos, mais de <span className="numerals">15</span> cursos lançados e milhares de pessoas transformadas.
                </>
              ) : (
                text
              )}
            </p>
          ))}
        </div>
      </motion.div>

      <Divider />

      {/* ── BLOCO 5 — O QUE É O PROTOCOLO ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', fontWeight: 600, lineHeight: 1.18, color: ACCENT_SOFT }}
        >
          O que é o Protocolo de Liberação e Cocriação
        </h2>

        <div className="space-y-5 max-w-2xl mx-auto">
          {[
            'Eu consigo fazer a leitura do seu campo vibracional à distância. Sinto angústia, pressa, desespero, dúvida, medo e padrões dos mais diversos tipos.',
            'Quero lhe dizer que a sua mudança pode ser mais rápida e sem que haja inúmeros atendimentos e horas que muitas vezes são necessários investir em tempo, dinheiro e disposição.',
            'Eu lhe mostro sua dor, a vibração negativa que está lhe prendendo, as frases que limitam sua evolução, quem você ainda não perdoou, quem lhe prende de forma consciente ou inconsciente. E lhe apresento uma forma simples de liberar tudo que lhe limita para que você viva a vida que merece.',
            'Não é mágica. É mudança de vibração.',
            'É lhe mostrar como abraçar sua dor e fazer com que ela seja liberada. Se trata de rasgar o véu que cobre sua percepção de você mesmo. Você nem tem ideia de quanto há frases, limites e dores que podem sair de forma simples.',
            'O que mais acontece é que muitas pessoas já estão totalmente mergulhadas nesse universo de autoconhecimento e expansão da consciência, mas não conseguem realmente mudar de vida. Praticam orações, decretos, fazem afirmações, práticas e até terapias, mas a raiz interna não é modificada e, de alguma forma, os padrões limitantes voltam a fazer parte da vida.',
            'O que levaria anos de terapia e inúmeras sessões para revelar e trabalhar, o Protocolo traz à tona em 126 dias, de forma profunda, cirúrgica e transformadora. Não é um atalho. É um processo que vai na raiz do que a terapia convencional muitas vezes não consegue alcançar, porque atua no campo vibracional enquanto você dorme, onde as resistências do consciente não interferem.',
            'O Protocolo atua fazendo uma investigação profunda em dores que estão escondidas dentro de você. Cada ciclo contempla questões específicas e cirúrgicas que vão apontar padrões enraizados a partir de você e que, se você fosse responder tudo isso pessoalmente em um atendimento, precisaria de horas.',
            'Outro ponto fundamental é que quando você preenche o formulário, isso por si só já faz você iniciar sua jornada corajosa de mudança, pois você traz à tona situações aparentemente esquecidas, mas que estão vivas ou reprimidas dentro de você, impactando totalmente a sua vibração, a sua vida e o seu crescimento.',
            'Faço isso através de 6 ciclos de 21 dias. Não é necessário ficar horas em atendimentos. Tudo que você faz é preencher um formulário detalhando situações passadas que estão em suas memórias.',
            'A sessão de liberação gravada em áudio é criada baseada totalmente no que você preencheu. Frases que parecem inofensivas e memórias marcantes revelam padrões negativos que operam de forma inconsciente, limitando seu crescimento.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{
                fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)',
                fontWeight: i === 3 ? 600 : 400,
                lineHeight: 1.85,
                color: i === 3 ? 'var(--ink)' : 'rgba(232,224,213,0.78)',
              }}
            >
              {i === 6 ? (
                <>
                  O que levaria anos de terapia e inúmeras sessões para revelar e trabalhar, o Protocolo traz à tona em <span className="numerals">126</span> dias, de forma profunda, cirúrgica e transformadora. Não é um atalho. É um processo que vai na raiz do que a terapia convencional muitas vezes não consegue alcançar, porque atua no campo vibracional enquanto você dorme, onde as resistências do consciente não interferem.
                </>
              ) : i === 9 ? (
                <>
                  Faço isso através de <span className="numerals">6</span> ciclos de <span className="numerals">21</span> dias. Não é necessário ficar horas em atendimentos. Tudo que você faz é preencher um formulário detalhando situações passadas que estão em suas memórias.
                </>
              ) : (
                text
              )}
            </p>
          ))}
        </div>
      </motion.div>

      <Divider />

      {/* ── BLOCO 6 — DEPOIMENTOS ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.9rem)', fontWeight: 600, lineHeight: 1.2, color: ACCENT_SOFT }}
        >
          O que já estão dizendo sobre o Protocolo
        </h2>
        <ProtocolWhatsappTestimonials accentSoft={ACCENT_SOFT} />
      </motion.div>

      <Divider />

      {/* ── BLOCO 7 — COMO A MEDITAÇÃO ATUA ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.6rem, 4vw, 2rem)', fontWeight: 600, lineHeight: 1.18, color: ACCENT_SOFT }}
        >
          Como a sessão de liberação atua em você:
        </h2>

        <div className="space-y-5 max-w-2xl mx-auto">
          {[
            {
              label: 'Reconhecer',
              text: 'você ouve e automaticamente começa a perceber seus limites, dores e crenças. Muitas pessoas choram. É a dor enraizada sendo vista, muitas vezes pela primeira vez.',
            },
            {
              label: 'Liberar',
              text: 'após reconhecer, você recebe comandos criados especificamente para você, para aliviar, soltar e elevar o que estava preso.',
            },
            {
              label: 'Reprogramar',
              text: 'a sessão abre portas mentais e cria novos caminhos para que sua vida seja diferente a partir do primeiro momento em que você ouve.',
            },
          ].map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-4 rounded-sm p-5 sm:p-6"
              style={{
                background: 'linear-gradient(135deg, rgba(185,163,217,0.08) 0%, rgba(185,163,217,0.02) 100%)',
                border: '1px solid rgba(185,163,217,0.2)',
              }}
            >
              <span style={{ color: ACCENT, fontSize: '1.1rem', flexShrink: 0, marginTop: '3px' }}>✦</span>
              <p className="font-body" style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.85)' }}>
                <strong style={{ color: ACCENT_SOFT, fontWeight: 600 }}>{item.label}:</strong>{' '}
                {item.text}
              </p>
            </div>
          ))}
        </div>

        <div className="space-y-5 max-w-2xl mx-auto mt-8">
          {[
            'A sessão de liberação é feita para você ouvir no horário em que vai dormir.',
            'Nesse estado de sonolência e relaxamento as resistências do seu consciente se baixam, e isso favorece totalmente a liberação, a reprogramação e a elevação do seu estado interno.',
            'É natural você começar a ouvir e entrar em relaxamento à medida que a sessão avança. Diversas pessoas que já fazem o Protocolo relatam que dormem melhor. Por isso, quer você ouça acordado ou dormindo, a sessão fará uma grande transformação.',
            'O que já foi relatado por várias pessoas é que surgem lembranças ao acordar e ao longo do dia de fatos e memórias que nem se lembravam. A verdade vai sendo vista e você percebe que havia tanta coisa negativa vibrando a partir de você.',
            'Quando você faz um processo terapêutico, muitas vezes fala uma única vez sobre uma dor, trauma ou circunstância do passado. Na sessão de liberação que você ouvirá durante os 21 dias é como se você, a cada ciclo, estivesse passando gradualmente por um terapeuta que sabe todas as dores que permeiam sua vida. E toda vez que você ouve a sessão, isso traz mais expressão, cura e liberação.',
            'Ao final dos 21 dias você é um novo ser, com uma nova consciência, vibração e vida.',
          ].map((text, i) => (
            <p
              key={i}
              className="font-body"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.85, color: 'rgba(232,224,213,0.78)' }}
            >
              {i === 4 ? (
                <>
                  Quando você faz um processo terapêutico, muitas vezes fala uma única vez sobre uma dor, trauma ou circunstância do passado. Na sessão de liberação que você ouvirá durante os <span className="numerals">21</span> dias é como se você, a cada ciclo, estivesse passando gradualmente por um terapeuta que sabe todas as dores que permeiam sua vida. E toda vez que você ouve a sessão, isso traz mais expressão, cura e liberação.
                </>
              ) : i === 5 ? (
                <>
                  Ao final dos <span className="numerals">21</span> dias você é um novo ser, com uma nova consciência, vibração e vida.
                </>
              ) : (
                text
              )}
            </p>
          ))}
        </div>

        <div
          className="relative rounded-sm p-6 sm:p-7 mt-8 max-w-2xl mx-auto"
          style={{
            background: 'linear-gradient(135deg, rgba(185,163,217,0.10) 0%, rgba(185,163,217,0.04) 100%)',
            border: '1px solid rgba(185,163,217,0.35)',
          }}
        >
          <div
            className="absolute left-0 inset-y-0 w-[3px] rounded-l-sm"
            style={{ background: `linear-gradient(180deg, ${ACCENT_SOFT}, ${ACCENT}, transparent)` }}
          />
          <p
            className="uppercase font-body mb-3"
            style={{ fontSize: '0.78rem', letterSpacing: '0.3em', color: ACCENT_SOFT, lineHeight: 1.6 }}
          >
            ✦ Gravado com presença real
          </p>
          <p
            className="font-body"
            style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'var(--ink)' }}
          >
            Não é inteligência artificial. É presença real.
          </p>
        </div>
      </motion.div>

      <Divider />

      {/* ── BLOCO 8 — OS 6 CICLOS ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <div className="text-center mb-10">
          <h2
            className="font-display mb-4"
            style={{ fontSize: 'clamp(1.7rem, 4vw, 2.1rem)', fontWeight: 600, lineHeight: 1.15, color: ACCENT_SOFT }}
          >
            Um processo que cresce com você.
          </h2>
          <p
            className="font-body max-w-xl mx-auto"
            style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.72)' }}
          >
            Cada ciclo de <span className="numerals">21</span> dias dissolve uma camada e prepara o terreno para a próxima. Você começa onde está. E avança no seu tempo.
          </p>
        </div>

        <div className="space-y-4">
          {CYCLES.map((cycle, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.4 + i * 0.08 }}
              className="flex items-start gap-4 rounded-sm p-5 sm:p-6"
              style={{
                background: 'linear-gradient(135deg, rgba(185,163,217,0.07) 0%, rgba(185,163,217,0.02) 100%)',
                border: '1px solid rgba(185,163,217,0.18)',
              }}
            >
              <span style={{ fontSize: '1.5rem', flexShrink: 0, lineHeight: 1.4 }}>{cycle.emoji}</span>
              <div>
                <p
                  className="font-display mb-1"
                  style={{ fontSize: 'clamp(1.2rem, 2.8vw, 1.4rem)', fontWeight: 500, lineHeight: 1.3, color: ACCENT_SOFT }}
                >
                  Ciclo <span className="numerals">{i + 1}</span>{' '}
                  <em className="italic font-light">— {cycle.title}</em>
                </p>
                <p
                  className="font-body"
                  style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', fontWeight: 400, lineHeight: 1.75, color: 'rgba(232,224,213,0.7)' }}
                >
                  {cycle.desc}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <Divider />

      {/* ── BLOCO 9 — OFERTA ── */}
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.3 }}
        className="mb-12 md:mb-16"
      >
        <div className="flex flex-col gap-6">

          <div
            className="rounded-sm p-6 sm:p-8 md:p-10 relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, rgba(200,146,58,0.08) 0%, rgba(200,146,58,0.02) 100%)',
              border: '1px solid rgba(200,146,58,0.4)',
            }}
          >
            <div
              className="absolute inset-x-0 -top-24 h-48 pointer-events-none"
              style={{ background: 'radial-gradient(closest-side, rgba(200,146,58,0.14), transparent)' }}
            />

            <div className="flex items-center gap-3 mb-6">
              <span
                className="uppercase font-body px-3 py-1 rounded-full"
                style={{ fontSize: '0.82rem', letterSpacing: '0.3em', color: '#E8C77A', border: '1px solid rgba(200,146,58,0.45)', background: 'rgba(200,146,58,0.10)', lineHeight: 1.7 }}
              >
                Comece sua jornada de transformação.
              </span>
            </div>

            <h3
              className="font-display mb-4"
              style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.85rem)', fontWeight: 500, lineHeight: 1.18, color: '#E8C77A' }}
            >
              O que está incluído em cada ciclo:
            </h3>

            <ul className="space-y-3 mb-5">
              {[
                'Um mapa escrito com tudo que está limitando você e o caminho de liberação',
                'Uma sessão de liberação gravada em áudio exclusivamente para você, baseada no que você revelou',
                'Frases para sair de toda negatividade que, ao afirmar, elevam você imediatamente',
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="flex-shrink-0" style={{ color: '#E8C77A', fontSize: '1.1rem', lineHeight: 1.6 }}>✦</span>
                  <span className="font-body" style={{ fontSize: 'clamp(1.1rem, 2.3vw, 1.2rem)', lineHeight: 1.7, color: 'rgba(232,224,213,0.8)' }}>
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <p
              className="font-body mb-6"
              style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.8, color: 'rgba(232,224,213,0.78)' }}
            >
              <span className="numerals">6</span> ciclos progressivos de <span className="numerals">21</span> dias. São <span className="numerals">126</span> dias de transformação camada por camada.
            </p>

            <div className="pt-6 border-t" style={{ borderColor: 'rgba(200,146,58,0.25)' }}>
              <p
                className="font-body mb-2"
                style={{ fontSize: 'clamp(1.35rem, 3.2vw, 1.7rem)', fontWeight: 600, lineHeight: 1.3, color: 'var(--ink)' }}
              >
                De{' '}
                <span className="numerals" style={{ textDecoration: 'line-through', color: 'rgba(232,224,213,0.45)', fontWeight: 400 }}>R$1.782</span>
                {' '}por{' '}
                <span className="numerals">R$997</span>
                {' '}à vista
              </p>
              <p
                className="font-body mb-6"
                style={{ fontSize: 'clamp(1.15rem, 2.5vw, 1.3rem)', fontWeight: 400, lineHeight: 1.4, color: 'rgba(232,224,213,0.55)' }}
              >
                ou <span className="numerals">12x</span> de <span className="numerals">R$103,11</span>
              </p>
              <a
                href={JORNADA_COMPLETA_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="block w-full text-center rounded-xl uppercase transition-all duration-300 hover:brightness-110 hover:-translate-y-[1px] active:translate-y-0"
                style={{
                  fontSize: 'clamp(1.2rem, 2.6vw, 1.35rem)',
                  fontWeight: 600,
                  letterSpacing: '0.04em',
                  padding: '20px 36px',
                  minHeight: '60px',
                  lineHeight: 1.5,
                  background: '#C8923A',
                  color: '#ffffff',
                  boxShadow: '0 6px 18px rgba(200,146,58,0.28), 0 1px 2px rgba(0,0,0,0.18)',
                  border: '1px solid rgba(255,255,255,0.08)',
                }}
              >
                Quero começar minha liberação
              </a>
              <p
                className="font-body text-center mt-4"
                style={{ fontSize: '0.95rem', fontWeight: 400, lineHeight: 1.6, color: 'rgba(232,224,213,0.55)', fontStyle: 'italic' }}
              >
                Os ciclos são entregues progressivamente, um a cada <span className="numerals">21</span> dias conforme você avança na jornada.
              </p>
            </div>
          </div>

        </div>
      </motion.div>

      <Divider />

      {/* ── BLOCO 10 — FAQ ── */}
      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, delay: 0.3 }}
        className="mb-16"
      >
        <h2
          className="font-display text-center mb-10"
          style={{ fontSize: 'clamp(1.5rem, 3.5vw, 1.9rem)', fontWeight: 600, lineHeight: 1.2, color: ACCENT_SOFT }}
        >
          Perguntas frequentes
        </h2>
        <div className="space-y-4">
          {FAQ_ITEMS.map((item, i) => (
            <FaqItem key={i} q={item.q} a={item.a} />
          ))}
        </div>
      </motion.div>

    </motion.div>
  )
}

export default PageProtocol
